// ─────────────────────────────────────────────────────────────────────────────
// ComponentShowcase.jsx
// Visual reference page for all Salient common components.
// Route: /showcase  (add this route in App.jsx temporarily during development)
//
// How to add to App.jsx:
//   import ComponentShowcase from '@pages/ComponentShowcase'
//   <Route path="showcase" element={<ComponentShowcase />} />
// ─────────────────────────────────────────────────────────────────────────────

import Button        from '@components/common/Button'
import SectionHeading from '@components/common/SectionHeading'
import Badge         from '@components/common/Badge'
import PageHero      from '@components/common/PageHero'
import Divider       from '@components/common/Divider'

// ── Local helpers ─────────────────────────────────────────────────────────────

function ShowSection({ title, children }) {
  return (
    <section style={{ marginBottom: '60px' }}>
      <div style={{
        display:       'flex',
        alignItems:    'center',
        gap:           '12px',
        marginBottom:  '28px',
        paddingBottom: '14px',
        borderBottom:  '2px solid #f0efe9',
      }}>
        <div style={{ width: '4px', height: '22px', background: '#ed9716', flexShrink: 0 }} />
        <h2 style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   '13px',
          fontWeight: 700,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          color:      '#6b6a63',
          margin:     0,
        }}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  )
}

function Row({ children, gap = '12px', wrap = true }) {
  return (
    <div style={{
      display:    'flex',
      alignItems: 'flex-start',
      flexWrap:   wrap ? 'wrap' : 'nowrap',
      gap,
    }}>
      {children}
    </div>
  )
}

function Label({ children }) {
  return (
    <p style={{
      fontFamily:  "'DM Sans', sans-serif",
      fontSize:    '11px',
      fontWeight:  600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color:       '#a8a79f',
      margin:      '0 0 8px',
    }}>
      {children}
    </p>
  )
}

function Box({ children, bg = '#ffffff', padding = '24px', style: s = {} }) {
  return (
    <div style={{
      background:   bg,
      padding,
      flex:         1,
      minWidth:     '280px',
      boxShadow:    '0 1px 3px rgba(0,0,0,0.06)',
      border:       '1px solid #e0dfda',
      ...s,
    }}>
      {children}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ComponentShowcase() {
  return (
    <div style={{ background: '#f9f9f7', minHeight: '100vh' }}>

      {/* ── Showcase Header ── */}
      <div style={{
        background:    '#1a1a18',
        padding:       '32px var(--container-padding)',
        maxWidth:      '100%',
        position:      'sticky',
        top:           0,
        zIndex:        50,
        borderBottom:  '3px solid #ed9716',
      }}>
        <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#ed9716', margin: '0 0 4px' }}>
              Salient Agrinutrition
            </p>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.5rem', fontWeight: 700, color: '#ffffff', margin: 0 }}>
              Component Showcase
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
              Development reference — remove route before production
            </span>
            <div style={{ width: '8px', height: '8px', background: '#32B700', borderRadius: '50%' }} />
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '52px var(--container-padding)' }}>

        {/* ════════════════════════════════
            BUTTONS
        ════════════════════════════════ */}
        <ShowSection title="Button — variants">
          <Row>
            <div>
              <Label>primary</Label>
              <Button variant="primary" to="/">Contact Us</Button>
            </div>
            <div>
              <Label>secondary</Label>
              <Button variant="secondary" to="/">Learn More</Button>
            </div>
            <div>
              <Label>outline</Label>
              <Button variant="outline" to="/">View Products</Button>
            </div>
            <div>
              <Label>outline-green</Label>
              <Button variant="outline-green" to="/">About Us</Button>
            </div>
            <div>
              <Label>ghost</Label>
              <Button variant="ghost" to="/">Read More</Button>
            </div>
          </Row>

          <div style={{ marginTop: '28px' }}>
            <Label>outline-white &amp; white (on dark background)</Label>
            <div style={{ background: '#065e32', padding: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outline-white" to="/">Get In Touch</Button>
              <Button variant="white" to="/">Contact Us</Button>
            </div>
          </div>
        </ShowSection>

        <ShowSection title="Button — sizes">
          <Row>
            <div><Label>sm</Label><Button size="sm" to="/">Small</Button></div>
            <div><Label>md (default)</Label><Button size="md" to="/">Medium</Button></div>
            <div><Label>lg</Label><Button size="lg" to="/">Large</Button></div>
            <div><Label>xl</Label><Button size="xl" to="/">Extra Large</Button></div>
          </Row>
        </ShowSection>

        <ShowSection title="Button — states">
          <Row>
            <div><Label>loading</Label><Button loading>Sending...</Button></div>
            <div><Label>disabled</Label><Button disabled>Disabled</Button></div>
            <div>
              <Label>no arrow</Label>
              <Button showArrow={false} to="/">No Arrow</Button>
            </div>
            <div>
              <Label>full width</Label>
              <div style={{ width: '240px' }}>
                <Button fullWidth to="/">Full Width</Button>
              </div>
            </div>
          </Row>
        </ShowSection>

        {/* ════════════════════════════════
            BADGES
        ════════════════════════════════ */}
        <ShowSection title="Badge — colors">
          <Row>
            <div><Label>green (default)</Label><Badge>Feed Additives</Badge></div>
            <div><Label>amber</Label><Badge color="amber">Performance Enhancers</Badge></div>
            <div><Label>dark</Label><Badge color="dark">Poultry</Badge></div>
            <div><Label>outline</Label><Badge color="outline">Germany</Badge></div>
          </Row>
          <div style={{ marginTop: '20px' }}>
            <Label>white (on dark bg)</Label>
            <div style={{ background: '#065e32', padding: '16px', display: 'inline-flex', gap: '8px' }}>
              <Badge color="white">Dairy</Badge>
              <Badge color="white">Ruminant</Badge>
            </div>
          </div>
        </ShowSection>

        <ShowSection title="Badge — sizes">
          <Row>
            <div><Label>sm (default)</Label><Badge size="sm">Feed Additives</Badge></div>
            <div><Label>md</Label><Badge size="md">Mineral Solutions</Badge></div>
          </Row>
        </ShowSection>

        {/* ════════════════════════════════
            DIVIDER
        ════════════════════════════════ */}
        <ShowSection title="Divider — variants">
          <Row gap="32px">
            <Box>
              <Label>amber / left (default)</Label>
              <Divider />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#6b6a63', margin: 0 }}>Section content below</p>
            </Box>
            <Box>
              <Label>amber / center</Label>
              <Divider align="center" />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#6b6a63', margin: 0, textAlign: 'center' }}>Centered content</p>
            </Box>
            <Box bg="#065e32">
              <Label style={{ color: 'rgba(255,255,255,0.5)' }}>white / on dark</Label>
              <Divider color="white" />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>Dark background content</p>
            </Box>
            <Box>
              <Label>green</Label>
              <Divider color="green" width="60px" />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#6b6a63', margin: 0 }}>Green variant</p>
            </Box>
          </Row>
        </ShowSection>

        {/* ════════════════════════════════
            SECTION HEADING
        ════════════════════════════════ */}
        <ShowSection title="SectionHeading — light theme">
          <Row gap="32px">
            <Box>
              <Label>left align (default)</Label>
              <SectionHeading
                tag="Our Products"
                title="Solutions Built for Sri Lanka's Livestock Industry"
                subtitle="We source and distribute premium feed additives, mineral solutions, and specialty nutritional inputs from internationally recognized manufacturers."
              />
            </Box>
            <Box>
              <Label>center align</Label>
              <SectionHeading
                tag="About Us"
                title="Who We Are"
                subtitle="A globally connected livestock nutrition partner committed to advancing Sri Lanka's animal production sector."
                align="center"
              />
            </Box>
          </Row>
        </ShowSection>

        <ShowSection title="SectionHeading — dark theme">
          <Row gap="32px">
            <Box bg="#065e32">
              <Label>dark / left</Label>
              <SectionHeading
                tag="Global Suppliers"
                title="Trusted International Partners"
                subtitle="We connect you to world-class nutritional technology from globally recognized manufacturers."
                theme="dark"
              />
            </Box>
            <Box bg="#065e32">
              <Label>dark / center</Label>
              <SectionHeading
                tag="Our Mission"
                title="Together, We Can Do Great Things"
                subtitle="Empowering Sri Lanka's farmers and livestock producers through science-driven nutrition."
                theme="dark"
                align="center"
              />
            </Box>
          </Row>
        </ShowSection>

        {/* ════════════════════════════════
            PAGE HERO
        ════════════════════════════════ */}
        <ShowSection title="PageHero — inner page banner">
          <div style={{ marginBottom: '24px' }}>
            <Label>with breadcrumbs / left align</Label>
            <PageHero
              tag="About Us"
              title="Who We Are"
              subtitle="A globally connected livestock nutrition partner committed to advancing Sri Lanka's animal production sector through science-driven, performance-focused solutions."
              breadcrumbs={[
                { label: 'Home', to: '/' },
                { label: 'About Us' },
              ]}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Label>center align / no breadcrumbs</Label>
            <PageHero
              tag="Our Products"
              title="Science-Driven Nutritional Solutions"
              subtitle="Premium feed additives, mineral solutions, and performance enhancers for poultry, dairy, and ruminant systems."
              align="center"
            />
          </div>

          <div>
            <Label>minimal / title only</Label>
            <PageHero
              title="News & Blog"
              breadcrumbs={[
                { label: 'Home', to: '/' },
                { label: 'News & Blog' },
              ]}
              minHeight="200px"
            />
          </div>
        </ShowSection>

        {/* ════════════════════════════════
            COLOUR PALETTE
        ════════════════════════════════ */}
        <ShowSection title="Brand Colour Palette">
          <Row gap="8px">
            {[
              { name: 'Green 700',  hex: '#007935', label: 'Primary' },
              { name: 'Green 800',  hex: '#065e32', label: 'Dark' },
              { name: 'Green 900',  hex: '#064d2a', label: 'Darkest' },
              { name: 'Amber 500',  hex: '#ed9716', label: 'Primary' },
              { name: 'Amber 600',  hex: '#cc7510', label: 'Dark' },
              { name: 'Amber 300',  hex: '#fbbf44', label: 'Light' },
              { name: 'Neutral 900',hex: '#2a2924', label: 'Text' },
              { name: 'Neutral 600',hex: '#6b6a63', label: 'Muted' },
              { name: 'Neutral 50', hex: '#f9f9f7', label: 'BG' },
            ].map(({ name, hex, label }) => (
              <div key={hex} style={{ textAlign: 'center', minWidth: '90px' }}>
                <div style={{
                  width:        '100%',
                  height:       '60px',
                  background:   hex,
                  border:       '1px solid rgba(0,0,0,0.08)',
                  marginBottom: '8px',
                }} />
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700, color: '#2a2924', margin: '0 0 2px' }}>{name}</p>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '10px', color: '#a8a79f', margin: '0 0 2px' }}>{hex}</p>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '10px', color: '#6b6a63', margin: 0 }}>{label}</p>
              </div>
            ))}
          </Row>
        </ShowSection>

        {/* ════════════════════════════════
            TYPOGRAPHY
        ════════════════════════════════ */}
        <ShowSection title="Typography">
          <Box>
            <div style={{ marginBottom: '24px' }}>
              <Label>Display - Playfair Display</Label>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 700, color: '#1a1a18', margin: 0, lineHeight: 1.15 }}>
                Advancing Livestock Nutrition
              </p>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Label>Heading H2</Label>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 700, color: '#1a1a18', margin: 0 }}>
                Science-Driven Solutions
              </h2>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Label>Heading H3</Label>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: '1.375rem', fontWeight: 700, color: '#1a1a18', margin: 0 }}>
                Feed Additives & Mineral Solutions
              </h3>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Label>Body — DM Sans</Label>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '1.0625rem', color: '#55544e', lineHeight: 1.75, margin: 0 }}>
                We source and distribute premium feed additives, mineral solutions, performance enhancers, gut health products, and specialty nutritional inputs from internationally recognized manufacturers.
              </p>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <Label>Small / Caption</Label>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '13px', color: '#88877f', lineHeight: 1.6, margin: 0 }}>
                Together, we can do great things — Salient Agrinutrition Pvt. Ltd.
              </p>
            </div>
            <div>
              <Label>Label / Tag</Label>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#ed9716', margin: 0 }}>
                Our Products
              </p>
            </div>
          </Box>
        </ShowSection>

        {/* Footer note */}
        <div style={{
          padding:      '24px',
          background:   '#1a1a18',
          textAlign:    'center',
        }}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
            Salient Agrinutrition — Component Showcase — Development Only
          </p>
        </div>

      </div>
    </div>
  )
}